import { useCallback, useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Show, SignIn, SignUp } from "@clerk/react";
import { Toaster } from "@/components/ui/sonner";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import Shell from "@/components/Shell";
import { StepUp, AcceptGate, AccountGate, NoOrg, Welcome } from "@/components/Gates";
import { useApi, stepUpToken, owedToken } from "@/lib/api";
import { MeContext, isManager } from "@/lib/me";
import { theme, useDarkApp } from "@/lib/theme";
import AuthLayout from "@/pages/AuthLayout";
import PendingPage from "@/pages/PendingPage";
import UploadPage from "@/pages/UploadPage";
import QueriesPage from "@/pages/QueriesPage";
import OverduePage from "@/pages/OverduePage";
import StatementsPage from "@/pages/StatementsPage";
import WholesalersPage from "@/pages/WholesalersPage";
import DocumentsPage from "@/pages/DocumentsPage";
import EmailsPage from "@/pages/EmailsPage";
import SummaryPage from "@/pages/SummaryPage";
import TeamPage from "@/pages/TeamPage";
import AccountPage from "@/pages/AccountPage";
import PricesPage from "@/pages/PricesPage";
import SpendPage from "@/pages/SpendPage";
import ApprovalsPage from "@/pages/ApprovalsPage";
import SettingsPage from "@/pages/SettingsPage";
import SignupPage, { SignupDone } from "@/pages/SignupPage";
import ConsolePage from "@/pages/ConsolePage";
import SiteLayout from "@/pages/site/SiteLayout";
import { Home, HowItWorks, Benefits, Pricing, Security } from "@/pages/site/Marketing";
import { LegalPage, LegalArchive, Resources, Resource, Contact } from "@/pages/site/Content";

const BLOCKED = ["unpaid", "suspended", "closed"];

function AppRoutes({ me }) {
  const mgr = isManager(me);
  const only = (el) => (mgr ? el : <Navigate to="/upload" replace />);
  return (
    <Routes>
      <Route element={<Shell />}>
        <Route path="/" element={<Navigate to={mgr ? "/queries" : "/upload"} replace />} />
        <Route path="/upload" element={<UploadPage />} />
        <Route path="/documents" element={<DocumentsPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/queries" element={only(<QueriesPage />)} />
        <Route path="/overdue" element={only(<OverduePage />)} />
        <Route path="/statements" element={only(<StatementsPage />)} />
        <Route path="/wholesalers" element={only(<WholesalersPage />)} />
        <Route path="/emails" element={only(<EmailsPage />)} />
        <Route path="/summary" element={only(<SummaryPage />)} />
        <Route path="/team" element={only(<TeamPage />)} />
        <Route path="/prices" element={only(<PricesPage />)} />
        <Route path="/spend" element={only(<SpendPage />)} />
        <Route path="/approvals" element={only(<ApprovalsPage />)} />
        <Route path="/settings" element={only(<SettingsPage />)} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function Member() {
  const api = useApi();
  const loc = useLocation();
  const [me, setMe] = useState(null);
  const [err, setErr] = useState(null);
  const [n, setN] = useState(0);
  const reload = useCallback(() => setN((x) => x + 1), []);
  useEffect(() => { api("get", "/me").then((m) => { theme.set(m.theme); setMe(m); }).catch((e) => setErr(e.message)); }, [api, n]);
  useEffect(() => { theme.on(); return theme.off; }, []);
  useEffect(() => {
    const h = () => { stepUpToken.set(""); reload(); };
    window.addEventListener("stepup-required", h);
    return () => window.removeEventListener("stepup-required", h);
  }, [reload]);
  if (err) return <p className="p-8 text-rose-700" data-testid="me-error">Could not load your account: {err}</p>;
  if (!me) return <div className="flex h-screen items-center justify-center text-slate-400" data-testid="app-loading">Loading…</div>;
  if (me.needs_step_up) return <StepUp me={me} onDone={reload} />;
  const body = (() => {
    if (me.admin && loc.pathname.startsWith("/console")) return <ConsolePage />;
    if (!me.org) return <NoOrg me={me} />;
    if (me.role === "pending") return <PendingPage me={me} />;
    if (BLOCKED.includes(me.access)) return <AccountGate me={me} />;
    if (me.needs_acceptance?.length) return <AcceptGate me={me} onDone={reload} />;
    return <AppRoutes me={me} />;
  })();
  return <MeContext.Provider value={me}>{body}</MeContext.Provider>;
}

const AppToaster = () => <Toaster position="top-center" richColors theme={useDarkApp() ? "dark" : "light"} />;

// signing out locks owed amounts and drops the sign-in code token
const SignedOutReset = ({ children }) => {
  useEffect(() => { if (owedToken.get()) owedToken.set(""); stepUpToken.set(""); }, []);
  return children;
};

const Protected = () => (
  <>
    <Show when="signed-in"><Member /></Show>
    <Show when="signed-out"><SignedOutReset><Navigate to="/sign-in" replace /></SignedOutReset></Show>
  </>
);

const HomeOrApp = () => (
  <>
    <Show when="signed-in"><Member /></Show>
    <Show when="signed-out"><SignedOutReset><SiteLayout /></SignedOutReset></Show>
  </>
);

export default function App() {
  return (
    <BrowserRouter>
      <ClerkAuthProvider>
        <Routes>
          <Route path="/sign-in/*" element={<AuthLayout><SignIn routing="path" path="/sign-in" signUpUrl="/signup" /></AuthLayout>} />
          <Route path="/sign-up/*" element={<AuthLayout><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /></AuthLayout>} />
          <Route path="/" element={<HomeOrApp />}><Route index element={<Home />} /></Route>
          <Route element={<SiteLayout />}>
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/benefits" element={<Benefits />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/security" element={<Security />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/resources/:slug" element={<Resource />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/legal/archive" element={<LegalArchive />} />
            <Route path="/legal/:slug" element={<LegalPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/signup/done" element={<SignupDone />} />
          </Route>
          <Route path="/*" element={<Protected />} />
        </Routes>
        <AppToaster />
      </ClerkAuthProvider>
    </BrowserRouter>
  );
}
